import { format } from 'date-fns';
import { poundSterlingSymbol, hasValue, countryCodesToCountryCodeNums, getLocaleDate } from '../lib/utils';

export const formatMoney = (value: number, currency: string): string => {
	return getCurrencySymbolUsingCurrencyNumber(currency) + value;
};

export const formatDateString = (date: Date | string) => {
	const originalDate = typeof date === 'string' ? new Date(date) : date;
	if (Number.isNaN(originalDate.getTime())) {
		return 'Referee has not answered';
	}
	return format(originalDate, 'dd/MM/yyyy');
};

export const getCurrencySymbolUsingCurrencyNumber = (currencyNumber: string): string => {
	switch (currencyNumber) {
		case '036': // AUD
		case '124': // CAD
		case '702': // SGD
		case '554': // NZD
		case '840': // USD
			return '$';
		case '826': // GBP
			return poundSterlingSymbol;
		case '978': // EUR
			return '€';
		default:
			throw new Error(`Unknown currency number: ${currencyNumber}.`);
	}
};

export const getCurrencySymbolUsingIsoCurrencyCode = (isoCurrencyCode: string): string => {
	switch (isoCurrencyCode) {
		case 'AUD':
		case 'NZD':
		case 'SGD':
		case 'CAD':
		case 'USD':
			return '$';
		case 'EUR':
			return '€';
		case 'GBP':
		default:
			return poundSterlingSymbol;
	}
};

export const getTaxType = (currency: string): string => {
	switch (currency) {
		case '036': // Australia
		case '124': // Canada
		case '702': // Singapore
		case '554': // New Zealand
			return 'GST';
		case '826': // UK
		case '978': // Ireland
			return 'VAT';
		case '840': // United States
			return 'Sales Tax';
		default:
			throw new Error(`Unknown tax: ${currency}.`);
	}
};

export const getCountryCodeForCurrency = (currency: string): string => {
	switch (currency) {
		case '036':
			return 'AUD'; // Australian Dollar
		case '124':
			return 'CAD'; // Canadian Dollar
		case '702':
			return 'SGD'; // Singapore Dollar
		case '554':
			return 'NZD'; // New Zealand Dollar
		case '840':
			return 'USD'; // US Dollar
		case '826':
			return 'GBP'; // British Pound Sterling
		case '978':
			return 'EUR'; // Euro
		default:
			throw new Error(`Unknown currency: ${currency}.`);
	}
};

export const getIsoCurrencyCodeForCountryCode = (countryCode: string): string => {
	switch (countryCode) {
		case 'AUS':
			return 'AUD';
		case 'GBR':
			return 'GBP';
		case 'NZL':
			return 'NZD';
		case 'IRL':
		case 'IE':
			return 'EUR';
		case 'SGP':
			return 'SGD';
		case 'CAN':
			return 'CAD';
		case 'USA':
			return 'USD';
		default:
			return 'GBP';
	}
};

export const formatPhoneNumber = (phoneNumber: string) => {
	const sgMobileLength = 11;
	const auMobileLength = 12;
	const ukMobileLength = 13;
	if (!hasValue(phoneNumber) || phoneNumber.length <= 3) {
		return '';
	}
	if (phoneNumber.length === sgMobileLength) {
		return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3, 7)} ${phoneNumber.slice(7)}`;
	}
	if (phoneNumber.length === auMobileLength) {
		return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 9)} ${phoneNumber.slice(9)}`;
	}
	if (phoneNumber.length === ukMobileLength) {
		return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3, 7)} ${phoneNumber.slice(7)}`;
	}
	return null;
};

export const formatPhoneNumberNew = (phoneNumber: string, countryCode: string) => {
	if (!hasValue(phoneNumber) || phoneNumber.length <= 3) {
		return '';
	}
	if (hasValue(countryCode)) {
		const countryCodeNum = countryCodesToCountryCodeNums[countryCode];
		if (hasValue(countryCodeNum)) {
			const nationalNumber = phoneNumber.slice(countryCodeNum.length);
			if (nationalNumber.length === 8) {
				return `${countryCodeNum} ${nationalNumber.slice(0, 4)} ${nationalNumber.slice(4)}`;
			}
			if (nationalNumber.length >= 9 && nationalNumber.length <= 11) {
				return `${countryCodeNum} ${nationalNumber.slice(0, 3)} ${nationalNumber.slice(3, 6)} ${nationalNumber.slice(
					6
				)}`;
			}
			if (nationalNumber.length >= 12 && nationalNumber.length <= 13) {
				return `${countryCodeNum} ${nationalNumber.slice(0, 4)} ${nationalNumber.slice(4, 8)} ${nationalNumber.slice(
					8
				)}`;
			}
		}
	}
	return formatPhoneNumber(phoneNumber);
};

export const getLocaleDateString = (isoDateString: string) => {
	if (isoDateString === null) {
		return null;
	}
	return (getLocaleDate(isoDateString) ?? new Date(isoDateString)).toLocaleDateString(getCultureString());
};

export const getLocaleTimeString = (isoDateString: string) => {
	if (isoDateString === null) {
		return null;
	}
	return new Date(isoDateString).toLocaleTimeString(getCultureString());
};

export const getCultureString = () => {
	// Alert: Some date string format is based on this hardcoded AU format, eg getLocaleDateFromFormatString(str, 'dd/MM/yyyy')
	// in DataCaptureField. We must update it if we change this getCultureString to get different date format in the future.
	// TODO: get the tag based on the User's currency setting. Wait for new microservice authentication so that we can get for candidate.
	// en-AU = Australian English
	// en-GB = British English
	return 'en-AU';
};
